import { DatePicker, TimePicker } from '@mui/lab';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import { LastStepVM, ParentVM, SectionVM } from 'civil-status/view-models/step-form';
import { RadioGroup } from '@mui/material';
import { ItemVM } from 'shell/view-models/item';
import { Checkbox } from '@mui/material';
import ListNumber from '../list/list-number';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import AutocompleteAuthority, {
  ID_MEDICINA_LEGALE,
  ID_OBITORIO_AUTHORITY,
  ID_OTHER_AUTHORITY,
  ID_PROCURA_AUTHORITY,
  ItemValoreDominioAttoVM,
} from '../list/list-authority';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ValoreDominioAttoVO } from 'shell/models/act-wizard';
import { AxiosResponse } from 'axios';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ID_REASON_IMPEDIMENT } from 'civil-status/constants/id-reason-impedimenti';
import { DateTime } from 'luxon';
import { DomainType } from 'civil-status/constants/domain';
import { CommunicationType } from 'civil-status/constants/communication-type';
import ListPlaceDeath, { ID_PLACE_DEATH } from '../list/list-place-death';
import { PmiButton } from 'shell/components/shared/button';
import { KeySubjects } from 'civil-status/constants/keys-subjects-section';
import { MUNICIPALITY_MILANO } from 'civil-status/constants/municipality-milano';

const DateCurrent: DateTime = DateTime.now();

interface DeadDataProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  keySection?: string;
  setDataInterprete: (isWithInterprete?: boolean) => void;
  formContext: FormikProps<LastStepVM>;
}

const DeadDataComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
  setDataInterprete,
  formContext,
}: DeadDataProps): React.ReactElement => {
  const isBaseDeco: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEDECO;
  }, [configuration]);

  const isBaseDecoV: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEDECOV;
  }, [configuration]);

  const isBaseDeca: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEDECA;
  }, [configuration]);

  const isDatiDeCest: boolean = useMemo(() => {
    return configuration === ConfigurationType.DATIDECEST;
  }, [configuration]);

  const isDatiDeCgen: boolean = useMemo(() => {
    return configuration === ConfigurationType.DATIDECGEN;
  }, [configuration]);

  const isBaseDecnid: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEDECNID;
  }, [configuration]);

  useEffect(() => {
    if (values?.conditionsItem?.id?.toString() === '3') {
      return;
    }

    onChange('reasonImpediment', undefined);
  }, [values?.conditionsItem]);

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values || {}).foreign,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChange('foreign', {
        ...(valuesNew || {}),
      });
    },
    [values, onChange]
  );

  const onChangeRadio = useEventCallback(() => {
    onChanges({
      isDatePresumed: !values?.isDatePresumed,
      dateDeath: null,
      timeDeath: null,
      notePresumed: '',
      datePresumedStart: null,
      datePresumedEnd: null,
    });
  });

  const onChangeRadioState = useEventCallback(() => {
    onChanges({
      isForeign: !values?.isForeign,
      italian: {
        municipality: undefined,
      },
      foreign: {
        state: undefined,
      },
    });
  });

  const handleListNumberChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('numberAppearing', event.target.value);
  });

  const handleListPlaceDeath = useEventCallback((event: SelectChangeEvent<string>) => {
    onChanges({ placeDeath: event.target.value, otherPlaceDeath: '' });
  });

  const handleListTypeCommunicationChange = useEventCallback((item?: ItemVM) => {
    onChanges({
      ...(values || {}),
      typeCommunication: item,
      numeroNullaOsta: '',
    });
  });

  const getDataAuthority = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onChangeListAuthority = useEventCallback((valueAuthority?: ItemValoreDominioAttoVM) => {
    if (
      isBaseDecoV || // questa configurazione non inizializza altri campi
      valueAuthority?.id === ID_OTHER_AUTHORITY ||
      valueAuthority?.id === ID_OBITORIO_AUTHORITY ||
      valueAuthority?.id === ID_PROCURA_AUTHORITY ||
      valueAuthority?.id === ID_MEDICINA_LEGALE ||
      !(valueAuthority?.idDominioPadre || valueAuthority?.idValorePadre)
    ) {
      onChanges({
        ...(values || {}),
        authority: valueAuthority,
        otherAuthority: '',
        placeDeath: '',
        address: '',
        italian: {
          municipality: undefined,
        },
      });
    } else {
      (
        getDataAuthority({
          url: `${__ACT_API_URL__}/valori-dominio-atti/id-dominio-padre/${valueAuthority?.idDominioPadre}/id-valore-padre/${valueAuthority?.idValorePadre}`,
        }) as unknown as ResponsePromise<
          HttpResponse<Array<ValoreDominioAttoVO>>,
          HttpResponse<Array<ValoreDominioAttoVO>>
        >
      ).then((response) => {
        const castedResponse: Array<ValoreDominioAttoVO> =
          (response as AxiosResponse<HttpResponse<Array<ValoreDominioAttoVO>>>).data.payload || [];

        const castedResponseMunicipality: ValoreDominioAttoVO | undefined = castedResponse.find(
          (item) => Boolean(item.idValoreAnsc)
        );
        const castedResponseAddress: ValoreDominioAttoVO | undefined = castedResponse.find(
          (item) => !Boolean(item.idValoreAnsc)
        );

        onChanges({
          ...(values || {}),
          authority: valueAuthority,
          otherAuthority: '',
          placeDeath: castedResponseAddress?.descrizioneBreveValore,
          address: castedResponseAddress?.valore,
          italian: castedResponseMunicipality
            ? {
                municipality: {
                  description: castedResponseMunicipality.valore || '',
                  province: castedResponseMunicipality.descrizioneBreveValore || '',
                  provinceCode: castedResponseMunicipality.descrizioneBreveValore || '',
                  id: castedResponseMunicipality.idValoreAnsc || '',
                  code: castedResponseMunicipality.idValoreAnsc
                    ? Number.parseInt(castedResponseMunicipality.idValoreAnsc)
                    : undefined,
                },
              }
            : { municipality: undefined },
        });
      });
    }
  });

  const disabledToAuthority: boolean = useMemo(() => {
    return Boolean(
      isBaseDeco &&
        values?.authority?.id &&
        values?.authority?.id !== ID_OTHER_AUTHORITY &&
        values?.authority?.id &&
        values?.authority?.id !== ID_OBITORIO_AUTHORITY &&
        values?.authority?.id &&
        values?.authority?.id !== ID_PROCURA_AUTHORITY &&
        values?.authority?.id &&
        values?.authority?.id !== ID_MEDICINA_LEGALE
    );
  }, [isBaseDeco, values?.authority]);

  const decedutoData: ParentVM = useMemo(() => {
    return formContext.values[KeySubjects.keyDeceduto];
  }, [formContext.values[KeySubjects.keyDeceduto]]);

  const disabledUploadResidenceData: boolean = useMemo(() => {
    if (
      decedutoData?.generality?.italianResidence?.municipality?.id === MUNICIPALITY_MILANO.id &&
      decedutoData?.generality?.address
    ) {
      return false;
    }

    return true;
  }, [decedutoData]);

  const onUploadResidenceData = useEventCallback(() => {
    const newAddress = decedutoData?.generality?.address || '';

    onChange('address', newAddress);
  });

  useEffect(() => {
    setDataInterprete(values?.isWithInterprete);
  }, [values?.isWithInterprete]);

  useEffect(() => {
    if (
      isBaseDeca &&
      values?.placeDeath &&
      values?.placeDeath !== ID_PLACE_DEATH.CASA &&
      values?.placeDeath !== ID_PLACE_DEATH.ALTRO &&
      !values.otherPlaceDeath
    ) {
      const otherPlaceDeath = values?.placeDeath;
      onChanges({ otherPlaceDeath, placeDeath: ID_PLACE_DEATH.ALTRO });
    }
  }, [isBaseDeca, values?.placeDeath]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Data o Data presunta"
            id="happeInItaly"
            value={values?.isDatePresumed || 'false'}
            onChange={() => {
              onChangeRadio();
            }}
          >
            <FormControlLabel value="false" control={<Radio />} label="Data" />
            <FormControlLabel value="true" control={<Radio />} label="Data presunta" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isDatePresumed ? (
        <Fragment>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data presunta inizio *"
              aria-label="Data presunta inizio"
              value={values?.datePresumedStart || null}
              onChange={(date) => {
                onChange('datePresumedStart', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="datePresumedStart"
                  fullWidth
                  helperText={errors?.datePresumedStart}
                  error={touched?.datePresumedStart && Boolean(errors?.datePresumedStart)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data presunta fine *"
              aria-label="Data presunta fine"
              value={values?.datePresumedEnd || null}
              onChange={(date) => {
                onChange('datePresumedEnd', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="datePresumedEnd"
                  fullWidth
                  helperText={errors?.datePresumedEnd}
                  error={touched?.datePresumedEnd && Boolean(errors?.datePresumedEnd)}
                />
              )}
            />
          </Grid>
        </Fragment>
      ) : (
        <Fragment>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data morte *"
              aria-label="Data morte"
              value={values?.dateDeath || null}
              onChange={(date) => {
                onChange('dateDeath', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateDeath"
                  fullWidth
                  helperText={errors?.dateDeath}
                  error={touched?.dateDeath && Boolean(errors?.dateDeath)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TimePicker
              label="Ora morte"
              value={values?.timeDeath || null}
              disabled={disabled}
              onChange={(value) => onChange('timeDeath', value)}
              renderInput={(params) => (
                <PmiTextField
                  id="timeDeath"
                  name="timeDeath"
                  {...params}
                  fullWidth
                  error={touched?.timeDeath && Boolean(errors?.timeDeath)}
                  helperText={errors?.timeDeath}
                />
              )}
            />
          </Grid>
        </Fragment>
      )}
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Note data e ora presunta"
          aria-label="Note data e ora presunta"
          id="notePresumed"
          name="notePresumed"
          fullWidth
          disabled={disabled}
          helperText={errors?.notePresumed}
          error={touched?.notePresumed && Boolean(errors?.notePresumed)}
          type="text"
          variant="outlined"
          value={values?.notePresumed || ''}
          onChange={(event) => {
            onChange('notePresumed', event.target.value);
          }}
        />
      </Grid>
      {isBaseDeco || isBaseDecoV ? (
        <Grid container spacing={2} item>
          <Grid item xs={12} md={6}>
            <AutocompleteAuthority
              value={values?.authority?.id?.toString() || ''}
              onChange={onChangeListAuthority}
              error={touched?.authority && Boolean(errors?.authority)}
              required={isBaseDeco || isBaseDecoV}
              dataReference={DateCurrent}
              onlyId={isBaseDecoV ? [0, 15] : undefined}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {values?.authority?.id === ID_OTHER_AUTHORITY ? (
              <PmiTextField
                label="Specificare altra autorità *"
                aria-label="Specificare altra autorità"
                id="otherAuthority"
                name="otherAuthority"
                fullWidth
                disabled={disabled}
                helperText={errors?.otherAuthority}
                error={touched?.otherAuthority && Boolean(errors?.otherAuthority)}
                type="text"
                variant="outlined"
                value={values?.otherAuthority || ''}
                onChange={(event) => {
                  onChange('otherAuthority', event.target.value);
                }}
              />
            ) : undefined}
          </Grid>
          <Grid item xs={12} md={4}>
            <ListAnsc
              label="Tipo comunicazione"
              idDominio={DomainType.CommunicationType?.toString()}
              value={values?.typeCommunication?.id?.toString() || ''}
              onChange={handleListTypeCommunicationChange}
              disabled={disabled}
              error={Boolean(errors?.typeCommunication) && touched?.typeCommunication}
              required
            />
          </Grid>
          {values?.typeCommunication?.id?.toString() === CommunicationType.NullaOsta &&
          (isBaseDeco || isBaseDecoV) ? (
            <Grid item xs={12} md={4}>
              <PmiTextField
                label="Numero nulla osta"
                aria-label="Numero nulla osta"
                id="numeroNullaOsta"
                name="numeroNullaOsta"
                fullWidth
                disabled={disabled}
                helperText={errors?.numeroNullaOsta}
                error={touched?.numeroNullaOsta && Boolean(errors?.numeroNullaOsta)}
                type="text"
                variant="outlined"
                value={values?.numeroNullaOsta || ''}
                onChange={(event) => {
                  onChange('numeroNullaOsta', event.target.value);
                }}
              />
            </Grid>
          ) : undefined}
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data comunicazione *"
              aria-label="Data comunicazione"
              value={values?.dateCommunication || null}
              onChange={(date) => {
                onChange('dateCommunication', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateCommunication"
                  fullWidth
                  helperText={errors?.dateCommunication}
                  error={touched?.dateCommunication && Boolean(errors?.dateCommunication)}
                />
              )}
            />
          </Grid>
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={12} container spacing={2}>
        {isBaseDeca ? (
          <Fragment>
            <Grid item xs={12} md={4}>
              <ListPlaceDeath
                value={values?.placeDeath || ''}
                onChange={handleListPlaceDeath}
                disabled={disabled || disabledToAuthority}
                error={Boolean(errors?.placeDeath) && touched?.placeDeath}
              />
            </Grid>
            {values?.placeDeath === ID_PLACE_DEATH.ALTRO ? (
              <Grid item xs={12} md={8}>
                <PmiTextField
                  label="Altro luogo morte"
                  aria-label="Altro luogo morte"
                  id="otherPlaceDeath"
                  name="otherPlaceDeath"
                  fullWidth
                  helperText={errors?.otherPlaceDeath}
                  error={touched?.otherPlaceDeath && Boolean(errors?.otherPlaceDeath)}
                  type="text"
                  variant="outlined"
                  value={values?.otherPlaceDeath || ''}
                  onChange={(event) => {
                    onChange('otherPlaceDeath', event.target.value);
                  }}
                />
              </Grid>
            ) : undefined}
          </Fragment>
        ) : (
          <Grid item xs={12}>
            <PmiTextField
              label="Luogo morte"
              aria-label="Luogo morte"
              id="placeDeath"
              name="placeDeath"
              fullWidth
              disabled={disabled || disabledToAuthority}
              helperText={errors?.placeDeath}
              error={touched?.placeDeath && Boolean(errors?.placeDeath)}
              type="text"
              variant="outlined"
              value={values?.placeDeath || ''}
              onChange={(event) => {
                onChange('placeDeath', event.target.value);
              }}
            />
          </Grid>
        )}
      </Grid>
      {isDatiDeCgen ? (
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Scelta stato"
              id="isForeign"
              defaultValue={false}
              value={values?.isForeign || false}
              onChange={() => onChangeRadioState()}
            >
              <FormControlLabel value="false" control={<Radio />} label="Italia" />
              <FormControlLabel value="true" control={<Radio />} label="Estero" />
            </RadioGroup>
          </FormControl>
        </Grid>
      ) : undefined}
      {isDatiDeCest || values?.isForeign ? (
        <Foreign
          disabledComponent={disabled || disabledToAuthority}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          labelState="Stato estero"
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          required={!(isBaseDecnid || isBaseDeco || isBaseDecoV)}
          isWithLocationForeign={isDatiDeCest || isDatiDeCgen}
        />
      ) : (
        <Italian
          disabledComponent={disabled || disabledToAuthority}
          values={values?.italian}
          onChange={onChangeItalian}
          onChanges={onChangesItalian}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality="Comune"
          labelProvince="Provincia"
          required={!(isBaseDecnid || isBaseDeco || isBaseDecoV)}
          fromDate={values?.dateDeath || null}
        />
      )}
      {isBaseDeca ? (
        <Grid item xs={12}>
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            onClick={() => onUploadResidenceData()}
            disabled={disabled || disabledUploadResidenceData}
          >
            Carica dati di residenza
          </PmiButton>
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={12}>
        <PmiTextField
          label="Indirizzo"
          aria-label="Indirizzo"
          id="address"
          name="address"
          fullWidth
          disabled={disabled || disabledToAuthority}
          helperText={errors?.address}
          error={touched?.address && Boolean(errors?.address)}
          type="text"
          variant="outlined"
          value={values?.address || ''}
          onChange={(event) => {
            onChange('address', event.target.value);
          }}
        />
      </Grid>
      {isBaseDeca ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            {values?.conditionsItem?.id?.toString() === ID_REASON_IMPEDIMENT ? (
              <PmiTextField
                aria-label="Motivo impedimento scrittura"
                label="Motivo impedimento scrittura"
                id="reasonImpediment"
                name="reasonImpediment"
                fullWidth
                disabled={disabled}
                helperText={errors?.reasonImpediment}
                error={touched?.reasonImpediment && Boolean(errors?.reasonImpediment)}
                type="text"
                variant="outlined"
                multiline
                value={values?.reasonImpediment || ''}
                onChange={(event) => {
                  onChange('reasonImpediment', event.target.value);
                }}
              />
            ) : undefined}
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isWithInterprete"
                  checked={values?.isWithInterprete || false}
                  onChange={() => {
                    onChange('isWithInterprete', !values?.isWithInterprete);
                  }}
                  inputProps={{ 'aria-label': 'Presenza interprete' }}
                  disabled={disabled}
                />
              }
              label="Presenza interprete"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ListNumber
              value={values?.numberAppearing || ''}
              onChange={handleListNumberChange}
              disabled={disabled}
              error={Boolean(errors?.numberAppearing) && touched?.numberAppearing}
              required
            />
          </Grid>
        </Fragment>
      ) : undefined}
    </Grid>
  );
};

DeadDataComponent.displayName = 'Form Dead Data component';

const DeadData = memo(DeadDataComponent);

export default DeadData;
